import React, { useRef } from "react";
import styles from "./Assessment2Quiz.module.css";
import quiz from "../../../json/Quiz.json";
import { CheckIcon } from "../icons/icons";
import Assessment2Shell, { RailLens } from "../Assessment2Shell/Assessment2Shell";

/* ------------------------------------------------------------------
   Assessment 2 — a self-contained, fully responsive quiz step that
   matches the supplied design: branded header with a segmented step
   bar, a left "section" rail, two questions per step on the right,
   and a Back / Continue footer. All copy + questions come from
   Quiz.json (the original assessment ignores the extra fields).
   ------------------------------------------------------------------ */

const cfg = quiz.assessment2;

// The anona display font is missing some glyphs (%, 4, and dashes), so those
// characters are split out and rendered in a fallback font that carries them.
const FALLBACK_GLYPHS = /([%4–—-])/g;
const Glyph = ({ text }) =>
  String(text)
    .split(FALLBACK_GLYPHS)
    .map((part, i) =>
      /^[%4–—-]$/.test(part) ? (
        <span key={i} className={styles.pct}>
          {part}
        </span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );

const Radio = ({ selected }) => (
  <span className={`${styles.radio} ${selected ? styles.radioOn : ""}`} aria-hidden="true">
    <CheckIcon size={11} />
  </span>
);

/* One question block — every question uses the same symmetric card grid. */
const QuestionBlock = ({ question, number, value, onChange }) => {
  const selectedLabel = value?.label;

  const select = (opt) => onChange(question.id, opt);

  const renderOptions = () => (
    <div className={styles.cardGrid} role="radiogroup" aria-label={question.content}>
      {question.options.map((opt) => {
        const selected = selectedLabel === opt.label;
        return (
          <button
            key={opt.label}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`${styles.gridCard} ${selected ? styles.cardOn : ""}`}
            onClick={() => select(opt)}
          >
            <span className={styles.gridLabel}>
              <Glyph text={opt.label} />
            </span>
            <Radio selected={selected} />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={styles.question}>
      <div className={styles.qHead}>
        <span className={styles.qBadge}>Q{number}</span>
        <h2 className={styles.qTitle}>
          <Glyph text={question.content} />
        </h2>
      </div>
      {question.hint && <p className={styles.qHint}>{question.hint}</p>}
      {renderOptions()}
    </div>
  );
};

const Assessment2Quiz = ({
  questions,
  startNumber,
  stepIndex,
  answers,
  onChange,
  onBack,
  onNext,
  onExit,
  pageComplete,
}) => {
  const totalSteps = cfg.totalSteps || 4;
  const step = cfg.steps[stepIndex] || cfg.steps[cfg.steps.length - 1];

  const questionRefs = useRef({});

  // Mobile only: after an answer is picked, bring the next question up into
  // view (or scroll to the bottom so Continue shows on the last question).
  const handleSelect = (qId, opt, gi) => {
    onChange(qId, opt, gi);
    if (gi !== undefined) return;
    const isMobile =
      typeof window !== "undefined" && window.matchMedia("(max-width: 980px)").matches;
    if (!isMobile) return;
    const idx = questions.findIndex((q) => q.id === qId);
    const next = questions[idx + 1];
    // Offset so the question stops just below the sticky navbar (not too high).
    const NAV_OFFSET = 135;
    window.requestAnimationFrame(() => {
      if (next && questionRefs.current[next.id]) {
        // Not the last question -> bring the next one up below the navbar.
        const el = questionRefs.current[next.id];
        const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_OFFSET;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else {
        // Last question -> only reveal the Back / Continue footer if it isn't
        // already visible (no jump to the very bottom).
        const footer = document.querySelector('[data-a2-footer]');
        if (!footer) return;
        const r = footer.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.bottom > vh || r.top < 0) {
          footer.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }
    });
  };

  const rail = (
    <>
      <span className={styles.railEyebrow}>{step.eyebrow}</span>
      <h1 className={styles.railTitle}>{step.title}</h1>
      <p className={styles.railText}>{step.description}</p>

      <RailLens />
    </>
  );

  return (
    <Assessment2Shell
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      stepLabel={`Step ${stepIndex + 1} of ${totalSteps}`}
      rail={rail}
      railFilled
      onExit={onExit}
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!pageComplete}
    >
      <div className={styles.questions}>
        {questions.map((q, i) => (
          <div key={q.id} ref={(el) => (questionRefs.current[q.id] = el)}>
            <QuestionBlock
              question={q}
              number={startNumber + i}
              value={answers[q.id]}
              onChange={handleSelect}
            />
          </div>
        ))}
      </div>
    </Assessment2Shell>
  
  );
};

export default Assessment2Quiz;
